import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef, ReactNode } from 'react';
import { logger } from '@/lib/logger';
import { User, Session } from '@supabase/supabase-js';
import { CEREBRO_AUTH_STORAGE_KEY, supabase } from '@/integrations/supabase/client';
import { clearAllGamificationCaches } from '@/services/communityGamification';

interface BanInfo {
  isBanned: boolean;
  reason?: string;
}

type AccessState = 'idle' | 'checking' | 'allowed' | 'denied' | 'error';

interface AccessDecision {
  can_access: boolean;
  access_status?: string | null;
}

interface RpcResult<T> {
  data: T;
  error: unknown | null;
}

const invokeAccessRpc = <T,>(functionName: string) =>
  (supabase.rpc as unknown as (name: string) => PromiseLike<RpcResult<T>>)(functionName);

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isBanned: boolean;
  banReason?: string;
  accessState: AccessState;
  accessStatus?: string;
  refreshAccess: () => Promise<void>;
  
  signUp: (email: string, password: string, displayName?: string, timezone?: string, guard?: { hp?: string; elapsedMs?: number; formToken?: string }) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPasswordForEmail: (email: string) => Promise<{ error: Error | null; alreadySent?: boolean }>;
  updatePassword: (newPassword: string, options?: { skipRefresh?: boolean }) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const getCachedSession = (): Session | null => {
    try {
      const raw = localStorage.getItem(CEREBRO_AUTH_STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as Partial<Session> & { user?: User };
      if (!parsed?.user) return null;

      return parsed as unknown as Session;
    } catch {
      return null;
    }
  };

  // Hydrate from localStorage first to avoid a full-screen loader flash on refresh.
  const initialSession = getCachedSession();

  const [user, setUser] = useState<User | null>(initialSession?.user ?? null);
  const [session, setSession] = useState<Session | null>(initialSession);
  const [loading, setLoading] = useState(!initialSession);
  const [isBanned, setIsBanned] = useState(false);
  const [banReason, setBanReason] = useState<string | undefined>(undefined);
  const [accessState, setAccessState] = useState<AccessState>(initialSession ? 'checking' : 'idle');
  const [accessStatus, setAccessStatus] = useState<string | undefined>(undefined);
  const accessRequestRef = useRef(0);

  // Resolve the server-authoritative Cérebro access contract. MUST be called outside the onAuthStateChange
  // callback (in a separate useEffect) — calling supabase queries inside
  // the auth callback can deadlock the client after tab suspension.
  const refreshAccess = useCallback(async () => {
    const requestId = ++accessRequestRef.current;
    setAccessState('checking');
    setAccessStatus(undefined);
    setIsBanned(false);
    setBanReason(undefined);

    try {
      const { error: reconciliationError } = await invokeAccessRpc<unknown>('reconcile_current_forjahub_identity');
      if (reconciliationError) throw reconciliationError;

      const { data, error } = await invokeAccessRpc<AccessDecision | AccessDecision[] | null>('current_forjahub_access');
      if (error) throw error;

      const access = Array.isArray(data) ? data[0] : data;
      if (!access || typeof access.can_access !== 'boolean') {
        throw new Error('Resposta inválida do contrato de acesso.');
      }
      if (accessRequestRef.current !== requestId) return;

      setAccessStatus(access.access_status || undefined);
      if (access?.can_access) {
        setAccessState('allowed');
        setIsBanned(false);
        setBanReason(undefined);
      } else {
        const reasonByStatus: Record<string, string> = {
          expired: 'Seu acesso à Forja Digital expirou.',
          cancelled: 'Seu acesso à Forja Digital está cancelado.',
          revoked: 'Seu acesso à Forja Digital foi revogado.',
          unlinked: 'Esta conta ainda não está vinculada a um aluno da Forja Digital.',
          none: 'Não encontramos um acesso ativo à Forja Digital para esta conta.',
        };
        setAccessState('denied');
        setIsBanned(true);
        setBanReason(reasonByStatus[access?.access_status] || 'Acesso à Forja Digital indisponível.');
      }
    } catch (err) {
      if (accessRequestRef.current !== requestId) return;
      console.error('[Auth] Error resolving Forja Hub access:', err);
      setAccessState('error');
      setAccessStatus('verification_error');
      setIsBanned(true);
      setBanReason('Não foi possível validar seu acesso com segurança. Tente novamente.');
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener BEFORE getting initial session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: string, newSession: Session | null) => {
        logger.log('[Auth] Event:', event);
        
        // Se for recuperação de senha, redirecionar para tela de reset
        if (event === 'PASSWORD_RECOVERY') {
          logger.log('[Auth] Password recovery detected, redirecting to reset page...');
          // Atualiza URL sem hard-reload (preserva estado da app).
          try {
            const url = new URL(window.location.href);
            if (url.pathname !== '/auth' || url.searchParams.get('mode') !== 'reset') {
              url.pathname = '/auth';
              url.searchParams.set('mode', 'reset');
              window.history.replaceState({}, '', url.toString());
            }
          } catch {
            // The auth event still updates state if URL parsing is unavailable.
          }
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setLoading(false);
          return;
        }
        
        // IMPORTANT: avoid re-setting `user`/`session` with new object references
        // when nothing meaningful changed. Tab-switch / token refresh fires
        // SIGNED_IN repeatedly with the same user id; if we update state with
        // a brand-new user object every time, every consumer (RoleProvider,
        // OnboardingGate, queries keyed by user.id) re-runs and the app
        // appears to "reload" on tab return. We only update when the user id
        // or access_token actually changes.
        setUser((prev) => {
          const nextUser = newSession?.user ?? null;
          if (prev?.id && nextUser?.id && prev.id === nextUser.id) {
            return prev; // keep stable reference
          }
          return nextUser;
        });
        setSession((prev) => {
          if (!newSession) return null;
          if (
            prev?.user?.id === newSession.user?.id &&
            prev?.access_token === newSession.access_token
          ) {
            return prev; // same session — keep stable reference
          }
          return newSession;
        });
        setLoading(false);

        // IMPORTANT: do NOT call any supabase query here. It can deadlock
        // the auth client (known issue after tab suspension). Ban check
        // is moved to a separate useEffect keyed off `user`.
        if (!newSession?.user) {
          accessRequestRef.current += 1;
          setAccessState('idle');
          setAccessStatus(undefined);
          setIsBanned(false);
          setBanReason(undefined);
          clearAllGamificationCaches();
          // Limpa cache de sessão "fantasma" no localStorage para evitar
          // que `getCachedSession()` re-hidrate uma sessão antiga após signOut.
          try {
            localStorage.removeItem(CEREBRO_AUTH_STORAGE_KEY);
          } catch {
            // Storage can be unavailable in hardened/private browser contexts.
          }
        }
      }
    );

    // Get initial session — with a hard timeout. If supabase.auth.getSession()
    // hangs (known deadlock after tab suspension / corrupted client state),
    // we fall back to the cached session already hydrated from localStorage
    // and release the global loader. Otherwise users get stuck on
    // "Preparando sua forja...".
    const SESSION_TIMEOUT_MS = 4000;
    let settled = false;
    Promise.race([
      supabase.auth.getSession().then(({ data }) => data.session ?? null),
      new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), SESSION_TIMEOUT_MS),
      ),
    ])
      .then((resolvedSession) => {
        settled = true;
        if (resolvedSession) {
          setSession(resolvedSession);
          setUser(resolvedSession.user);
        }
        // Either way release the loader. If the request was slow, the
        // onAuthStateChange listener will reconcile state shortly after.
        setLoading(false);
      })
      .catch(() => {
        settled = true;
        setLoading(false);
      });

    // Hard safety net in case Promise.race itself never resolves.
    const safety = setTimeout(() => {
      if (!settled) setLoading(false);
    }, SESSION_TIMEOUT_MS + 1000);

    return () => {
      clearTimeout(safety);
      subscription.unsubscribe();
    };
  }, []);

  // Ban check runs OUTSIDE the auth callback to avoid deadlocks.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    void refreshAccess();
  }, [userId, session?.access_token, refreshAccess]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    displayName?: string,
    timezone?: string,
    guard?: { hp?: string; elapsedMs?: number; formToken?: string },
  ) => {
    // User-initiated self-service signup in Cérebro Auth. There is no invite,
    // mass provisioning or call to the legacy Resend/Lovable function.
    try {
      void guard;
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
          data: {
            display_name: displayName || undefined,
            timezone: timezone || 'America/Sao_Paulo',
          },
        },
      });
      if (error) {
        return { error: new Error(error.message || 'Não foi possível concluir o cadastro.') };
      }
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Erro ao conectar ao servidor.') };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('O servidor demorou muito para responder. Tente novamente em alguns instantes.')), 15000)
      );
      const authPromise = supabase.auth.signInWithPassword({ email, password });
      const { error } = await Promise.race([authPromise, timeoutPromise]);
      return { error };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Erro ao conectar ao servidor.') };
    }
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const resetPasswordForEmail = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset`,
      });
      return {
        error: error ? new Error(error.message || 'Falha ao enviar e-mail') : null,
        alreadySent: false,
      };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Erro ao conectar ao servidor.') };
    }
  }, []);

  const updatePassword = useCallback(async (newPassword: string, options?: { skipRefresh?: boolean }) => {
    const { data: sessionData } = await supabase.auth.getSession();
    logger.log('[updatePassword] Current session:', sessionData?.session ? 'valid' : 'none');
    
    if (!sessionData?.session) {
      console.error('[updatePassword] No active session');
      return { error: new Error('Sessão expirada. Faça login novamente.') };
    }
    
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    logger.log('[updatePassword] Result:', { success: !!data?.user, error: error?.message });
    
    if (!error && data?.user && !options?.skipRefresh) {
      logger.log('[updatePassword] Refreshing session...');
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.warn('[updatePassword] Session refresh warning:', refreshError.message);
      }
    }
    
    return { error };
  }, []);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    isBanned,
    banReason,
    accessState,
    accessStatus,
    refreshAccess,
    signUp,
    signIn,
    signOut,
    resetPasswordForEmail,
    updatePassword,
  }), [user, session, loading, isBanned, banReason, accessState, accessStatus, refreshAccess, signUp, signIn, signOut, resetPasswordForEmail, updatePassword]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
